import type { Availability, Schedule } from '@/types/tournament.model';
import { timeToMinutes } from '@/utils/time';

/**
 * Flattens every schedule block of a tournament into a single day-by-day calendar.
 *
 * A tournament can hold more than one block — a club that splits the group days and the finals day
 * into two, or several clubs hosting it — and `tournament.schedule` arrives in no particular order,
 * because it is a `Set` on the backend. Reading `schedule[0]` therefore shows an arbitrary subset
 * of the days: with a "Friday+Saturday" block and a "Sunday" block, which one you get is whatever
 * Hibernate returned first.
 *
 * The player app never hits this. `GET /tournaments/{id}` defaults to `squashSchedules=true` and
 * the backend merges the blocks for it (`TournamentFacade.orderSchedulesTimeRange`). The manager
 * endpoint keeps them apart on purpose — the schedule card and the edit modal need the per-club
 * blocks — so whoever wants the plain calendar has to merge them here.
 *
 * Days are keyed by date and ranges deduped by start/end: two blocks declaring the same hour are
 * the same hour on the same courts, not two of them. The club is dropped on purpose — a team's
 * availability is dates and hours, it never names a club.
 */
export function mergeScheduleBlocks(
  blocks: Schedule[] | undefined | null,
): Availability[] {
  if (!blocks || blocks.length === 0) return [];

  const byDate = new Map<string, Availability>();

  blocks.forEach((block) => {
    (block?.schedule ?? []).forEach((day) => {
      if (!day?.date) return;

      const existing = byDate.get(day.date);
      if (!existing) {
        byDate.set(day.date, {
          date: day.date,
          dayOfWeek: day.dayOfWeek,
          timeRange: [...(day.timeRange ?? [])],
        });
        return;
      }

      (day.timeRange ?? []).forEach((range) => {
        const alreadyDeclared = existing.timeRange.some(
          (r) => r.startTime === range.startTime && r.endTime === range.endTime,
        );
        if (!alreadyDeclared) existing.timeRange.push(range);
      });
    });
  });

  return Array.from(byDate.values())
    .map((day) => ({
      ...day,
      timeRange: [...day.timeRange].sort(
        (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
      ),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * The last day of the calendar — the one a SHORT tournament reserves for the knockout bracket.
 *
 * The backend reads it off the whole schedule (`ShortTournamentScheduleStrategy.resolveKnockoutSlot`
 * takes the max date across every block), so it has to be computed over the merged calendar and
 * never over a single block, or the manager and the fixture generator disagree about which day the
 * finals are on.
 *
 * Dates are `YYYY-MM-DD`, so comparing them as strings is the same as comparing them as dates and
 * skips any timezone parsing.
 */
export function getFinalDay(days: Availability[]): string | null {
  if (days.length === 0) return null;
  return days.reduce(
    (latest, day) => (day.date > latest ? day.date : latest),
    days[0].date,
  );
}

/** Slot from which a SHORT tournament plays its knockout bracket (club-local date and time). */
export interface KnockoutStart {
  /** `YYYY-MM-DD` */
  date: string;
  /** `HH:mm` */
  startTime: string;
}

/** The backend may serialize times as HH:mm:ss; the cut and the slots are compared as HH:mm. */
const toHourMinute = (time: string): string => time.slice(0, 5);

/** Reads the knockout cut off a schedule or tournament; null unless both parts are present. */
export function knockoutStartOf(source: {
  knockoutStartDate?: string | null;
  knockoutStartTime?: string | null;
}): KnockoutStart | null {
  if (!source.knockoutStartDate || !source.knockoutStartTime) return null;
  return {
    date: source.knockoutStartDate,
    startTime: toHourMinute(source.knockoutStartTime),
  };
}

/** The cut a SHORT tournament plays with, as the backend reads it: the earliest cut among its blocks. */
export function earliestKnockoutStart(cuts: (KnockoutStart | null)[]): KnockoutStart | null {
  return cuts.reduce<KnockoutStart | null>((min, cut) => {
    if (!cut) return min;
    if (!min || cut.date < min.date || (cut.date === min.date && cut.startTime < min.startTime)) {
      return cut;
    }
    return min;
  }, null);
}

/**
 * Whether a slot belongs to the knockout bracket. With a cut it is every slot from the cut onwards;
 * without one it falls back to the legacy rule (the last day of the calendar).
 * Dates and times are zero-padded, so string comparison is chronological.
 */
export function isKnockoutSlot(
  date: string,
  startTime: string,
  finalDay: string | null,
  knockoutStart: KnockoutStart | null,
): boolean {
  if (knockoutStart) {
    return (
      date > knockoutStart.date ||
      (date === knockoutStart.date && toHourMinute(startTime) >= knockoutStart.startTime)
    );
  }
  return date === finalDay;
}

/**
 * Label for where a block plays the bracket: "SOLO LLAVES" when its earliest slot already is
 * bracket, "LLAVES DESDE SÁB 20:00" from its own cut, or "LLAVES TODO EL DOM" when it holds the
 * final day. Null when none of its slots is bracket.
 * `finalDay` is the tournament's last day and only applies when no block has a cut; a block listed
 * outside a tournament has none.
 */
export function knockoutBlockLabel(
  knockoutStart: KnockoutStart | null,
  slots: { date: string; startTime: string }[],
  finalDay: string | null = null,
): string | null {
  if (!knockoutStart) {
    if (!finalDay || !slots.some((slot) => slot.date === finalDay)) return null;
    return slots.every((slot) => slot.date === finalDay)
      ? 'SOLO LLAVES'
      : `LLAVES TODO EL ${weekdayOf(finalDay).toUpperCase()}`;
  }
  const earliest = slots.reduce<{ date: string; startTime: string } | null>(
    (min, slot) => {
      const startTime = toHourMinute(slot.startTime);
      if (!min || slot.date < min.date || (slot.date === min.date && startTime < min.startTime)) {
        return { date: slot.date, startTime };
      }
      return min;
    },
    null,
  );
  if (
    earliest &&
    earliest.date === knockoutStart.date &&
    earliest.startTime === knockoutStart.startTime
  ) {
    return 'SOLO LLAVES';
  }
  return `LLAVES DESDE ${formatKnockoutStart(knockoutStart).toUpperCase()}`;
}

/** Day tag: "LLAVES" when every slot of the day is bracket, "LLAVES DESDE 20:00" when it starts mid-day. */
export function knockoutDayLabel(
  date: string,
  startTimes: string[],
  finalDay: string | null,
  knockoutStart: KnockoutStart | null,
): string | null {
  const times = startTimes.map(toHourMinute).sort();
  const knockoutTimes = times.filter((time) =>
    isKnockoutSlot(date, time, finalDay, knockoutStart),
  );
  if (knockoutTimes.length === 0) return null;
  return knockoutTimes.length === times.length ? 'LLAVES' : `LLAVES DESDE ${knockoutTimes[0]}`;
}

const weekdayOf = (date: string): string =>
  new Date(date + 'T00:00:00')
    .toLocaleDateString('es-AR', { weekday: 'short' })
    .replace(/\.$/, '');

/** "sáb 20:00", or "sáb 07/11 20:00" with the date. */
export function formatKnockoutStart(start: KnockoutStart, withDate = false): string {
  const weekday = weekdayOf(start.date);
  if (!withDate) return `${weekday} ${start.startTime}`;
  const [, month, day] = start.date.split('-');
  return `${weekday} ${day}/${month} ${start.startTime}`;
}
